import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { usePageStore } from '@/stores/pageStore';
import { Factory, LogIn, GraduationCap, UserCog, Sparkles, KeyRound, Eye, EyeOff } from 'lucide-react';

/**
 * 登录页 (v3 大赛版)
 * - 主推"游客体验"：评委/用户无需账号即可体验全部功能
 * - 游客可选择"学员视角"或"教师视角"进入
 * - 提供"演示账号"快捷登录（学生/教师各一个），方便体验完整后端功能
 * - 底部保留手动账号密码登录入口
 */
export const LoginPage: React.FC = () => {
  const login = useAuthStore((s) => s.login);
  const error = useAuthStore((s) => s.error);
  const setError = useAuthStore((s) => s.setError);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError('请输入账号和密码');
      return;
    }
    setLoading(true);
    try {
      await login(username.trim(), password);
    } catch {
      /* error 已在 store 里设置 */
    } finally {
      setLoading(false);
    }
  };

  /** 使用演示账号快速登录 */
  const quickLogin = async (user: string, pass: string) => {
    setUsername(user);
    setPassword(pass);
    setError(null);
    setLoading(true);
    try {
      await login(user, pass);
    } catch {
      /* error 已在 store 里设置 */
    } finally {
      setLoading(false);
    }
  };

  /** 进入游客模式 */
  const enterGuest = (role: 'student' | 'teacher') => {
    usePageStore.getState().setPage('gallery');
    useAuthStore.setState({
      user: {
        id: 'guest',
        username: 'guest',
        display_name: role === 'teacher' ? '游客·教师视角' : '游客·学员视角',
        student_no: null,
        role,
        class_id: null,
        must_change_pw: false,
        created_at: Date.now(),
        last_login_at: Date.now(),
      },
      status: 'ready',
      error: null,
    });
  };

  return (
    <div
      className="h-screen w-screen flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: 'url("/login-bg-hd.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        imageRendering: 'auto',
      }}
    >
      {/* 动态光效叠加层 */}
      <div className="pointer-events-none absolute inset-0 login-bg-overlays">
        <div className="login-red-conveyor login-red-conveyor-1" />
        <div className="login-red-conveyor login-red-conveyor-2" />
        <div className="login-red-scanner" />
        <div className="login-green-blob login-green-blob-1" />
        <div className="login-green-blob login-green-blob-2" />
        <div className="login-green-blob login-green-blob-3" />
        <div className="login-green-blob login-green-blob-4" />
        <div className="login-green-blob login-green-blob-5" />
        <div className="login-green-blob login-green-blob-6" />
        <div className="login-green-blob login-green-blob-7" />
        <div className="login-green-blob login-green-blob-8" />
        <div className="login-green-blink login-green-blink-1" />
        <div className="login-green-blink login-green-blink-2" />
      </div>

      <style>{`
        .login-bg-overlays { mix-blend-mode: screen; z-index: 1; }
        @keyframes redPulse {
          0%,100% { opacity: .55; filter: blur(30px); }
          50%     { opacity: .95; filter: blur(24px); }
        }
        .login-red-conveyor {
          position: absolute;
          height: 12%;
          border-radius: 50%;
          background: radial-gradient(ellipse at center,
            rgba(248,113,113,.85) 0%,
            rgba(239,68,68,.65) 30%,
            rgba(220,38,38,.35) 55%,
            rgba(0,0,0,0) 80%);
          animation: redPulse 3.2s ease-in-out infinite;
          will-change: opacity, filter;
        }
        .login-red-conveyor-1 { left: 8%; right: 32%; top: 62%; animation-delay: 0s; }
        .login-red-conveyor-2 { left: 42%; right: 8%; top: 74%; animation-delay: 1.4s; }
        @keyframes redScan {
          0% { left: 0%; opacity: 0; }
          8% { opacity: 1; }
          92% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        .login-red-scanner {
          position: absolute; top: 65%; width: 12%; height: 8%; border-radius: 50%;
          background: radial-gradient(ellipse at center,
            rgba(254,215,170,.95) 0%, rgba(251,146,60,.85) 25%,
            rgba(234,88,12,.55) 50%, rgba(0,0,0,0) 80%);
          filter: blur(18px); animation: redScan 5.5s linear infinite; animation-delay: .3s;
          will-change: left, opacity;
        }
        @keyframes greenBreath {
          0%,100% { transform: scale(.85); opacity: .5; }
          50%     { transform: scale(1.15); opacity: .95; }
        }
        .login-green-blob {
          position: absolute; width: 220px; height: 220px; border-radius: 50%;
          background: radial-gradient(circle at center,
            rgba(134,239,172,.85) 0%, rgba(34,197,94,.55) 30%,
            rgba(22,163,74,.25) 55%, rgba(0,0,0,0) 80%);
          filter: blur(28px); animation: greenBreath 2s ease-in-out infinite;
          will-change: transform, opacity;
        }
        .login-green-blob-1 { left: 4%; top: 40%; width: 220px; height: 220px; animation-delay: 0s; animation-duration: 2.0s; }
        .login-green-blob-2 { left: 22%; top: 30%; width: 180px; height: 180px; animation-delay: .6s; animation-duration: 2.4s; }
        .login-green-blob-3 { left: 66%; top: 46%; width: 200px; height: 200px; animation-delay: 1.1s; animation-duration: 1.7s; }
        .login-green-blob-4 { left: 82%; top: 34%; width: 240px; height: 240px; animation-delay: .3s; animation-duration: 2.1s; }
        .login-green-blob-5 { left: 12%; top: 68%; width: 160px; height: 160px; animation-delay: 1.5s; animation-duration: 2.6s; }
        .login-green-blob-6 { left: 34%; top: 82%; width: 200px; height: 200px; animation-delay: .9s; animation-duration: 1.9s; }
        .login-green-blob-7 { left: 74%; top: 78%; width: 180px; height: 180px; animation-delay: 1.8s; animation-duration: 2.3s; }
        .login-green-blob-8 { left: 90%; top: 60%; width: 160px; height: 160px; animation-delay: .4s; animation-duration: 1.8s; }
        @keyframes greenBlink {
          0%, 20% { opacity: .9; transform: scale(1); }
          25%, 45% { opacity: .15; transform: scale(.75); }
          50%, 70% { opacity: .9; transform: scale(1); }
          75%, 95% { opacity: .25; transform: scale(.8); }
          100% { opacity: .9; transform: scale(1); }
        }
        .login-green-blink {
          position: absolute; width: 80px; height: 80px; border-radius: 50%;
          background: radial-gradient(circle at center,
            rgba(187,247,208,.95) 0%, rgba(74,222,128,.75) 25%,
            rgba(34,197,94,.35) 55%, rgba(0,0,0,0) 80%);
          filter: blur(14px); animation: greenBlink 1.6s ease-in-out infinite;
          will-change: transform, opacity;
        }
        .login-green-blink-1 { left: 50%; top: 22%; animation-delay: .0s; animation-duration: 1.6s; }
        .login-green-blink-2 { left: 58%; top: 88%; animation-delay: .7s; animation-duration: 2.1s; }
        @media (prefers-reduced-motion: reduce) {
          .login-red-conveyor, .login-red-scanner, .login-green-blob, .login-green-blink { animation: none !important; opacity: .7; }
        }
      `}</style>

      {/* 背景遮罩 */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 520px 620px at 50% 50%, rgba(10,14,26,.55) 0%, rgba(10,14,26,.35) 45%, rgba(10,14,26,0) 75%)' }} />
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, rgba(10,14,26,.35) 0%, rgba(10,14,26,0) 15%, rgba(10,14,26,0) 85%, rgba(10,14,26,.4) 100%)' }} />

      {/* 品牌角标 */}
      <div className="absolute top-6 left-8 flex items-center gap-2 text-white/80 select-none z-10">
        <Factory className="w-6 h-6 text-primary" />
        <div>
          <div className="text-sm font-bold tracking-wider">CraftForge</div>
          <div className="text-[11px] text-white/50 tracking-widest -mt-0.5">MASTER · FORGE · INTELLIGENCE</div>
        </div>
      </div>

      {/* 主卡片 */}
      <div className="w-[480px] max-w-[92vw] bg-bg-secondary/85 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl overflow-hidden relative z-10">
        {/* 头部 */}
        <div className="bg-gradient-to-r from-primary/30 to-primary/10 px-8 py-7 flex items-center gap-4 border-b border-white/10">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg">
            <Factory className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary tracking-tight">匠魂实训引擎</h1>
            <p className="text-sm text-text-muted mt-1">CraftForge · 工业 AI 实训平台</p>
          </div>
        </div>

        {/* === 游客体验区（主推） === */}
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-text-primary">游客体验 · 无需账号</span>
          </div>
          <p className="text-xs text-text-muted mb-4">
            选择视角进入，可随时在顶栏切换角色，体验全部场景和功能
          </p>
          <div className="grid grid-cols-2 gap-3">
            {/* 学员视角 */}
            <button
              type="button"
              onClick={() => enterGuest('student')}
              className="group flex flex-col items-center gap-2 p-4 bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/60 rounded-xl transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-primary/20 group-hover:bg-primary/30 flex items-center justify-center transition-colors">
                <GraduationCap className="w-5 h-5 text-primary" />
              </div>
              <div className="text-sm font-semibold text-text-primary">学员视角</div>
              <div className="text-[11px] text-text-muted text-center">体验实训演练<br/>故障排查 · AI 指导</div>
            </button>
            {/* 教师视角 */}
            <button
              type="button"
              onClick={() => enterGuest('teacher')}
              className="group flex flex-col items-center gap-2 p-4 bg-accent/10 hover:bg-accent/20 border border-accent/30 hover:border-accent/60 rounded-xl transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-accent/20 group-hover:bg-accent/30 flex items-center justify-center transition-colors">
                <UserCog className="w-5 h-5 text-accent" />
              </div>
              <div className="text-sm font-semibold text-text-primary">教师视角</div>
              <div className="text-[11px] text-text-muted text-center">查看教学看板<br/>数据分析 · 班级管理</div>
            </button>
          </div>
        </div>

        {/* 分割线 */}
        <div className="px-8 py-1">
          <div className="border-t border-white/10" />
        </div>

        {/* === 演示账号快捷登录 === */}
        <div className="px-8 py-3">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="w-4 h-4 text-text-muted" />
            <span className="text-sm text-text-muted">演示账号快捷登录（体验完整后端功能）</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* 学生账号 */}
            <button
              type="button"
              onClick={() => quickLogin('student', '123456')}
              disabled={loading}
              className="group flex items-center gap-3 p-3 bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/60 rounded-xl transition-all disabled:opacity-50"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/20 group-hover:bg-primary/30 flex items-center justify-center transition-colors shrink-0">
                <GraduationCap className="w-4 h-4 text-primary" />
              </div>
              <div className="text-left min-w-0">
                <div className="text-sm font-semibold text-text-primary">学生账号</div>
                <div className="text-[11px] text-text-muted truncate">student / 123456</div>
              </div>
            </button>

            {/* 教师账号 */}
            <button
              type="button"
              onClick={() => quickLogin('teacher', '123456')}
              disabled={loading}
              className="group flex items-center gap-3 p-3 bg-accent/10 hover:bg-accent/20 border border-accent/30 hover:border-accent/60 rounded-xl transition-all disabled:opacity-50"
            >
              <div className="w-9 h-9 rounded-lg bg-accent/20 group-hover:bg-accent/30 flex items-center justify-center transition-colors shrink-0">
                <UserCog className="w-4 h-4 text-accent" />
              </div>
              <div className="text-left min-w-0">
                <div className="text-sm font-semibold text-text-primary">教师账号</div>
                <div className="text-[11px] text-text-muted truncate">teacher / 123456</div>
              </div>
            </button>
          </div>
          {error && (username === 'student' || username === 'teacher') && (
            <div className="mt-3 px-4 py-2.5 bg-warning/15 border border-warning/40 rounded-lg text-xs text-warning leading-relaxed">
              演示账号登录失败——请确认后端服务已启动（端口 3001）。也可使用上方"游客体验"模式（功能完整，无需后端）。
            </div>
          )}
        </div>

        {/* === 账号登录区（折叠） === */}
        <details className="px-8 py-3">
          <summary className="cursor-pointer text-sm text-text-muted hover:text-text-secondary flex items-center gap-2 select-none">
            <LogIn className="w-4 h-4" />
            其他账号登录（手动输入）
          </summary>
          <form onSubmit={handleLogin} className="pt-4 space-y-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2 font-medium">账号 / 学号</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="请输入账号"
                autoComplete="username"
                className="w-full px-4 py-3 bg-bg-tertiary/80 border border-border rounded-lg text-text-primary text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2 font-medium">密码</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-11 bg-bg-tertiary/80 border border-border rounded-lg text-text-primary text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && !(username === 'student' || username === 'teacher') && (
              <div className="px-4 py-3 bg-danger/15 border border-danger/40 rounded-lg text-sm text-danger">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary hover:bg-primary/90 text-white rounded-lg text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors shadow-lg shadow-primary/20"
            >
              <LogIn className="w-5 h-5" />
              {loading ? '登录中...' : '登 录'}
            </button>
          </form>
        </details>

        {/* 底部信息 */}
        <div className="px-8 pb-5 pt-2 text-xs text-text-muted text-center border-t border-white/10">
          12 个工业场景 · 3D 实景漫游 · AI 师傅实时指导 · 工艺动力学引擎
        </div>
      </div>

      {/* 底部品牌信息 */}
      <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-white/40 tracking-wider select-none z-10">
        © 2026 CraftForge · 匠造熔炉 · TRAE AI 创造力大赛作品
      </div>
    </div>
  );
};
